import { Fragment, memo, useCallback, useMemo } from "react";
import {
  BookOpen,
  Brain,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
} from "lucide-react";
import { LegendList } from "@legendapp/list/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { foldersService, type LibraryFolder } from "@/services/foldersService";
import type { MastermindSpace } from "@shared/schema";
import type { Source } from "@/services/sourcesService";
import type { DocumentActionsItem } from "@/components/DocumentActionsMenuContent";
import { LibraryDocumentRow, type LibraryDocumentActions } from "@/components/LibraryDocumentRow";
import { useSourceOfTruth } from "@/hooks/useSourceOfTruth";
import { collectSpaceIdsForCounts } from "@/lib/kbDocumentLinks";
import {
  folderNavRowClassName,
  folderNavRowSelectedClassName,
  folderNavLabelClassName,
  librarySectionHeadingClassName,
  spaceNavRowClassName,
  spaceNavRowSelectedClassName,
  spaceNavLabelClassName,
} from "@/components/library/libraryNavStyles";

/** Below this row count, skip Legend List virtualizer overhead (Profiler: LegendListInner dominated ~300ms commits). */
const LIBRARY_TREE_SIMPLE_LIST_MAX_ROWS = 50;

const libraryListScrollClassName =
  "flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden overscroll-contain mt-1 ml-4";

type LibraryItem = {
  id: string;
  title?: string | null;
  folderId?: string | null;
  spaceId?: string | null;
  isLocked?: boolean;
  includeInRag?: boolean;
  type?: string | null;
};

type FolderItem = DocumentActionsItem;

type LibraryFlatRow =
  | { type: "sectionHeading"; rowKey: string; label: string }
  | { type: "folder"; rowKey: string; folder: LibraryFolder; level: number }
  | { type: "document"; rowKey: string; item: FolderItem; paddingLeft: string }
  | { type: "emptyFolder"; rowKey: string; paddingLeft: string }
  | { type: "space"; rowKey: string; space: MastermindSpace }
  | { type: "spaceMore"; rowKey: string; count: number; paddingLeft: string }
  | { type: "empty"; rowKey: string };

interface LibraryTreeProps {
  docSearchQuery: string;
  folders: LibraryFolder[];
  kbItems: LibraryItem[];
  sources: Source[];
  librarySpaces: MastermindSpace[];
  expandedFolderIds: Set<string>;
  selectedFolderId: string | null;
  onToggleFolderExpanded: (folderId: string) => void;
  onDocumentClick: (documentId: string, isLocked?: boolean) => void;
  onFolderClick: (folderId: string) => void;
  onRootClick: () => void;
  onSpacesClick?: () => void;
  onSpaceClick: (spaceId: string) => void;
  embeddingStatusMap?: Record<string, boolean>;
  documentActions?: LibraryDocumentActions;
}

export const LibraryTree = memo(function LibraryTree({
  docSearchQuery,
  folders,
  kbItems,
  sources,
  librarySpaces,
  expandedFolderIds,
  selectedFolderId,
  onToggleFolderExpanded,
  onDocumentClick,
  onFolderClick,
  onRootClick,
  onSpaceClick,
  embeddingStatusMap,
  documentActions,
}: LibraryTreeProps) {
  const { docIds: sourceOfTruthDocIds } = useSourceOfTruth();
  const sourceOfTruthIdSet = useMemo(() => new Set(sourceOfTruthDocIds), [sourceOfTruthDocIds]);

  const rootFolders = useMemo(() => {
    return foldersService.buildFolderTree(folders);
  }, [folders]);

  const folderItemCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const items = new Map<string, FolderItem[]>();

    kbItems.forEach((item) => {
      if (item.folderId) {
        const count = counts.get(item.folderId) || 0;
        counts.set(item.folderId, count + 1);

        const folderItems = items.get(item.folderId) || [];
        folderItems.push({
          id: item.id,
          title: item.title || "Untitled Note",
          itemType: "note",
          type: item.type ?? "note",
          folderId: item.folderId,
          includeInRag: item.includeInRag !== false,
          isLocked: item.isLocked === true,
        });
        items.set(item.folderId, folderItems);
      }
    });

    sources.forEach((source) => {
      if (source.folderId) {
        const count = counts.get(source.folderId) || 0;
        counts.set(source.folderId, count + 1);

        const folderItems = items.get(source.folderId) || [];
        folderItems.push({
          id: source.id,
          title: source.title || "Untitled Reference",
          itemType: "reference",
          type: (source as { type?: string | null }).type ?? null,
          folderId: (source as { folderId?: string | null }).folderId ?? null,
          url: (source as { url?: string | null }).url ?? null,
          tags: (source as { tags?: string[] | null }).tags ?? null,
        });
        items.set(source.folderId, folderItems);
      }
    });

    return { counts, items };
  }, [kbItems, sources]);

  const getFolderItemCount = (folderId: string): number => {
    return folderItemCounts.counts.get(folderId) || 0;
  };

  const getFolderItems = (folderId: string): FolderItem[] => {
    return folderItemCounts.items.get(folderId) || [];
  };

  const spaceItemCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const items = new Map<string, FolderItem[]>();

    kbItems.forEach((item) => {
      for (const spaceId of collectSpaceIdsForCounts(item)) {
        const count = counts.get(spaceId) || 0;
        counts.set(spaceId, count + 1);

        const spaceItems = items.get(spaceId) || [];
        spaceItems.push({
          id: item.id,
          title: item.title || "Untitled Note",
          itemType: "note",
          type: item.type ?? "note",
          folderId: item.folderId ?? null,
          includeInRag: item.includeInRag !== false,
          isLocked: item.isLocked === true,
        });
        items.set(spaceId, spaceItems);
      }
    });

    return { counts, items };
  }, [kbItems]);

  const getSpaceItemCount = (spaceId: string): number => {
    return spaceItemCounts.counts.get(spaceId) || 0;
  };

  const getSpaceItems = (spaceId: string): FolderItem[] => {
    return spaceItemCounts.items.get(spaceId) || [];
  };

  const unfiledItems = useMemo(() => {
    const items: FolderItem[] = [];

    kbItems.forEach((item) => {
      if (!item.folderId) {
        items.push({
          id: item.id,
          title: item.title || "Untitled Note",
          itemType: "note",
          type: item.type ?? "note",
          folderId: null,
          includeInRag: item.includeInRag !== false,
          isLocked: item.isLocked === true,
        });
      }
    });

    sources.forEach((source) => {
      if (!(source as { folderId?: string | null }).folderId) {
        items.push({
          id: source.id,
          title: source.title || "Untitled Reference",
          itemType: "reference",
          type: (source as { type?: string | null }).type ?? null,
          folderId: null,
          url: (source as { url?: string | null }).url ?? null,
          tags: (source as { tags?: string[] | null }).tags ?? null,
        });
      }
    });

    return items;
  }, [kbItems, sources]);

  const flatRows = useMemo((): LibraryFlatRow[] => {
    const rows: LibraryFlatRow[] = [];
    const q = docSearchQuery.trim().toLowerCase();
    const matches = (s: string) => !q || s.toLowerCase().includes(q);

    if (!expandedFolderIds.has("root")) {
      return rows;
    }

    const rootFoldersFiltered = rootFolders.filter((folder) => matches(folder.name));
    const dedupedSpaces = Array.from(new Map(librarySpaces.map((s) => [s.id, s])).values());
    const spacesFiltered = dedupedSpaces.filter((space) => matches(space.name));
    const unfiledFiltered = unfiledItems.filter(
      (item) => !q || (item.title ?? "").toLowerCase().includes(q)
    );

    const isEmpty =
      rootFoldersFiltered.length === 0 &&
      unfiledFiltered.length === 0 &&
      spacesFiltered.length === 0;

    if (isEmpty) {
      rows.push({ type: "empty", rowKey: "empty-state" });
      return rows;
    }

    function addFolderRecursive(folder: LibraryFolder, level: number, filterFolderName: boolean) {
      if (filterFolderName && !matches(folder.name)) {
        return;
      }

      const childFolders = foldersService.getChildFolders(folders, folder.id);
      const itemCount = getFolderItemCount(folder.id);
      const folderItems = getFolderItems(folder.id);
      const hasChildren = childFolders.length > 0;
      const hasItems = itemCount > 0;
      const isExpanded = expandedFolderIds.has(folder.id);

      rows.push({ type: "folder", rowKey: `folder-h-${folder.id}`, folder, level });

      if (!isExpanded) {
        return;
      }

      if (hasChildren) {
        for (const child of childFolders) {
          addFolderRecursive(child, level + 1, false);
        }
      }
      if (hasItems) {
        for (const item of folderItems) {
          rows.push({
            type: "document",
            rowKey: `doc-${item.id}-folder-${folder.id}`,
            item,
            paddingLeft: `${(level + 1) * 16 + 18}px`,
          });
        }
      } else if (!hasChildren) {
        rows.push({
          type: "emptyFolder",
          rowKey: `empty-folder-${folder.id}`,
          paddingLeft: `${(level + 1) * 16 + 18}px`,
        });
      }
    }

    if (rootFoldersFiltered.length > 0) {
      rows.push({ type: "sectionHeading", rowKey: "heading-folders", label: "Folders" });
    }
    for (const folder of rootFoldersFiltered) {
      addFolderRecursive(folder, 0, true);
    }

    if (spacesFiltered.length > 0) {
      rows.push({ type: "sectionHeading", rowKey: "heading-spaces", label: "Spaces" });
    }
    for (const space of spacesFiltered) {
      const spaceItemCount = getSpaceItemCount(space.id);
      const spaceItems = getSpaceItems(space.id);
      const hasItems = spaceItemCount > 0;
      rows.push({ type: "space", rowKey: `space-h-${space.id}`, space });

      if (!hasItems || !expandedFolderIds.has(`space-${space.id}`)) {
        continue;
      }
      const shown = spaceItems.slice(0, 10);
      for (const item of shown) {
        rows.push({
          type: "document",
          rowKey: `doc-${item.id}-space-${space.id}`,
          item,
          paddingLeft: "34px",
        });
      }
      if (spaceItems.length > 10) {
        rows.push({
          type: "spaceMore",
          rowKey: `space-more-${space.id}`,
          count: spaceItems.length - 10,
          paddingLeft: "34px",
        });
      }
    }

    for (const item of unfiledFiltered) {
      rows.push({
        type: "document",
        rowKey: `doc-${item.id}-unfiled`,
        item,
        paddingLeft: "18px",
      });
    }

    return rows;
  }, [
    docSearchQuery,
    expandedFolderIds,
    folders,
    kbItems,
    librarySpaces,
    rootFolders,
    unfiledItems,
    folderItemCounts,
    spaceItemCounts,
  ]);

  const getEstimatedItemSize = useCallback((item: LibraryFlatRow) => {
    switch (item.type) {
      case "sectionHeading":
        return 28;
      case "folder":
        return 40;
      case "space":
        return 42;
      case "document":
        return 36;
      case "emptyFolder":
        return 28;
      case "spaceMore":
        return 26;
      case "empty":
        return 48;
      default:
        return 36;
    }
  }, []);

  const renderFlatRow = useCallback(
    (row: LibraryFlatRow) => {
      switch (row.type) {
        case "sectionHeading": {
          return (
            <p key={row.rowKey} className={cn(librarySectionHeadingClassName, "px-2 pt-2")}>
              {row.label}
            </p>
          );
        }
        case "empty": {
          return (
            <div className="px-2 py-2 text-sm text-muted-foreground text-center">
              {docSearchQuery ? "No items found" : "No folders or documents yet"}
            </div>
          );
        }
        case "emptyFolder": {
          return (
            <div
              className="px-2 py-1 text-xs text-muted-foreground italic"
              style={{ paddingLeft: row.paddingLeft }}
            >
              Empty folder
            </div>
          );
        }
        case "spaceMore": {
          return (
            <div className="px-2 py-1 text-xs text-muted-foreground" style={{ paddingLeft: row.paddingLeft }}>
              +{row.count} more
            </div>
          );
        }
        case "document": {
          return (
            <LibraryDocumentRow
              item={row.item}
              paddingLeft={row.paddingLeft}
              embeddingStatusMap={embeddingStatusMap}
              documentActions={documentActions}
              onDocumentClick={onDocumentClick}
              isSourceOfTruth={sourceOfTruthIdSet.has(row.item.id)}
            />
          );
        }
        case "space": {
          const spaceItemCount = getSpaceItemCount(row.space.id);
          const hasItems = spaceItemCount > 0;
          const isSpaceExpanded = expandedFolderIds.has(`space-${row.space.id}`);
          return (
            <div key={row.space.id}>
              <div
                className={cn(
                  spaceNavRowClassName,
                  "min-w-0",
                  selectedFolderId === `space-${row.space.id}` && spaceNavRowSelectedClassName
                )}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 p-0 shrink-0"
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleFolderExpanded(`space-${row.space.id}`);
                  }}
                >
                  {hasItems ? (
                    isSpaceExpanded ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-50" />
                  )}
                </Button>
                <div className="flex items-center gap-2 flex-1 min-w-0" onClick={() => onSpaceClick(row.space.id)}>
                  <Brain className="h-4 w-4 text-purple-500 shrink-0" />
                  <span className={spaceNavLabelClassName}>{row.space.name}</span>
                  {spaceItemCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="text-xs h-5 px-1.5 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 shrink-0"
                    >
                      {spaceItemCount}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          );
        }
        case "folder": {
          const { folder, level } = row;
          const childFolders = foldersService.getChildFolders(folders, folder.id);
          const itemCount = getFolderItemCount(folder.id);
          const hasChildren = childFolders.length > 0;
          const hasItems = itemCount > 0;
          const isExpanded = expandedFolderIds.has(folder.id);
          const isSelected = selectedFolderId === folder.id;

          return (
            <div
              className={cn(
                folderNavRowClassName,
                isSelected && folderNavRowSelectedClassName
              )}
              style={{ paddingLeft: level > 0 ? `${level * 12 + 8}px` : "8px" }}
              onClick={() => onFolderClick(folder.id)}
            >
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 p-0 shrink-0 hover:bg-accent/50 opacity-100"
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleFolderExpanded(folder.id);
                }}
              >
                {hasChildren || hasItems ? (
                  isExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  )
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-50" />
                )}
              </Button>
              {isExpanded ? (
                <FolderOpen className="h-4 w-4 text-amber-500 shrink-0" />
              ) : (
                <Folder className="h-4 w-4 text-amber-500 shrink-0" />
              )}
              <span className={folderNavLabelClassName}>{folder.name}</span>
              {itemCount > 0 && (
                <Badge variant="secondary" className="text-xs h-5 px-1.5 shrink-0">
                  {itemCount}
                </Badge>
              )}
            </div>
          );
        }
        default:
          return null;
      }
    },
    [
      docSearchQuery,
      documentActions,
      embeddingStatusMap,
      expandedFolderIds,
      folders,
      onDocumentClick,
      onFolderClick,
      onSpaceClick,
      onToggleFolderExpanded,
      selectedFolderId,
    ]
  );

  return (
    <div className="p-2 flex flex-col h-full min-h-0 min-w-0">
      <div
        className={cn(
          "shrink-0 flex items-center justify-between gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent transition-colors",
          selectedFolderId === null && "bg-accent"
        )}
      >
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 p-0.5"
          onClick={(event) => {
            event.stopPropagation();
            onToggleFolderExpanded("root");
          }}
        >
          {expandedFolderIds.has("root") ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
        <div className="flex items-center gap-2 flex-1" onClick={onRootClick}>
          <BookOpen className="h-4 w-4 text-blue-500" />
          <span className="text-sm font-medium">My Library</span>
        </div>
      </div>

      {expandedFolderIds.has("root") &&
        (flatRows.length <= LIBRARY_TREE_SIMPLE_LIST_MAX_ROWS ? (
          <div className={libraryListScrollClassName}>
            {flatRows.map((row) => (
              <Fragment key={row.rowKey}>{renderFlatRow(row)}</Fragment>
            ))}
          </div>
        ) : (
          <LegendList<LibraryFlatRow>
            className={libraryListScrollClassName}
            data={flatRows}
            keyExtractor={(item) => item.rowKey}
            getEstimatedItemSize={getEstimatedItemSize}
            recycleItems={false}
            estimatedItemSize={38}
            drawDistance={250}
            renderItem={({ item }) => <>{renderFlatRow(item)}</>}
          />
        ))}
    </div>
  );
});

LibraryTree.displayName = "LibraryTree";
